<template>
  <el-container
    direction="vertical"
    style="
      margin: 5vh 5vw;
      max-height: 80vh;
      overflow-y: auto;
      overflow-x: hidden;
    "
  >
    <h2 style="margin-right: 20px">Task: {{ currentTaskName }}</h2>
    <el-breadcrumb
      separator-class="el-icon-arrow-right"
      style="margin: 5px 0 35px 0"
    >
      <el-breadcrumb-item :to="{ path: `/servers/${instanceId}/tasks` }"
        >Tasks</el-breadcrumb-item
      >
      <el-breadcrumb-item>Edit Task</el-breadcrumb-item>
    </el-breadcrumb>
    <el-row style="margin-right: 25px">
      <el-col :span="24">
        <general-card
          ref="general"
          @nameUpdated="nameUpdated"
          @descriptionUpdated="descriptionUpdated"
          @typeSelected="typeSelected"
          @runTask="runTask"
          @runQuickTask="runQuickTask"
          @saveTask="saveTask"
          @deleteTask="deleteTask"
          @viewControlObjectsUpdated="viewControlObjectsUpdated"
          :taskName="taskName"
          :taskDescription="taskDescription"
          :taskType="taskType"
          :taskCheckValid="taskCheckValid"
          :taskViewControlObjects="taskViewControlObjects"
        />
      </el-col>
    </el-row>
    <br />
    <el-row :gutter="20" style="margin-right: 15px">
      <el-col :span="12">
        <source-card
          ref="sourceCard"
          @sourceTypeSelected="sourceTypeSelected"
          @factorUpdated="factorUpdated"
          @addDim="addDim"
          @removeDim="removeDim"
          @getCubeDims="getCubeDims"
          @dimAdded="dimAdded"
          @dimSelected="dimSelected"
          @selectionSubmitted="selectionSubmitted"
          @sourceValueUpdated="sourceValueUpdated"
          @selectAllUpdated="selectAllUpdated"
          @cubeListUpdated="cubeListUpdated"
          :taskType="taskType"
          :taskFactor="taskFactor"
          :taskSourceType="taskSourceType"
          :taskSourceCube="taskSourceCube"
          :taskSourceCubeDims="taskSourceCubeDims"
          :taskSourceDims="taskSourceDims"
          :taskSourceValue="taskSourceValue"
          :taskTargetCubeDims="taskTargetCubeDims"
          :taskCubes="taskCubes"
          :taskDims="taskDims"
          :taskCheckValid="taskCheckValid"
          :taskFilteredCubes="taskFilteredCubes"
          :taskSourceCubeList="taskSourceCubeList"
          :taskSelectAll="taskSelectAll"
        />
      </el-col>
      <el-col :span="12">
        <target-card
          ref="targetCard"
          @targetTypeSelected="targetTypeSelected"
          @getCubeDims="getCubeDims"
          @dimSelected="dimSelected"
          @selectionSubmitted="selectionSubmitted"
          @methodSelected="methodSelected"
          @showMappingPreview="showMappingPreview"
          :taskType="taskType"
          :taskSourceType="taskSourceType"
          :taskSourceCube="taskSourceCube"
          :taskSourceCubeDims="taskSourceCubeDims"
          :taskSourceDims="taskSourceDims"
          :taskTargetType="taskTargetType"
          :taskMethod="taskMethod"
          :taskTargetCube="taskTargetCube"
          :taskTargetCubeDims="taskTargetCubeDims"
          :taskTargetDims="taskTargetDims"
          :taskCubes="taskCubes"
          :taskDims="taskDims"
          :taskCheckValid="taskCheckValid"
        />
      </el-col>
    </el-row>
    <selector
      @selectionSubmitted="selectionSubmitted"
      @showMappingPreview="showMappingPreview"
      :taskType="taskType"
      :placeholder="placeholder"
      :dim="dimSelection"
      :dimIndex="dimSelectionIndex"
      :dimType="dimSelectionType"
      :taskSourceType="taskSourceType"
      :sourceElsIn="taskSourceCubeDims"
      :targetElsIn="taskTargetCubeDims"
    />
    <mapping-preview
      :sourceDim="mappingSourceDim"
      :targetDim="mappingTargetDim"
    />
  </el-container>
</template>

<script>
import { mapState } from "vuex";
import TaskService from "../services/TaskService";
import GeneralCard from "../components/cards/General";
import SourceCard from "../components/cards/Source";
import TargetCard from "../components/cards/Target";
import Selector from "../components/modals/Selector";
import Helper from "../utilities/Helper";
import MappingPreview from "../components/modals/MappingPreview";

export default {
  components: { GeneralCard, SourceCard, TargetCard, Selector, MappingPreview },
  computed: {
    ...mapState([
      "userId",
      "instanceId",
      "taskId",
      "currentTaskName",
      "isUserLoggedIn",
      "environmentId",
      "envRights",
    ]),
  },
  data() {
    return {
      placeholder: null,
      taskName: null,
      taskDescription: null,
      taskType: "Copy",
      taskFactor: 1,
      taskSourceType: "Single Cube",
      taskSourceCube: null,
      taskSourceCubeDims: [],
      taskSourceDims: [],
      taskSourceValue: 0,
      taskTargetType: "Same Cube",
      taskMethod: "Replace",
      taskTargetCube: null,
      taskTargetCubeDims: [],
      taskTargetDims: [],
      taskCubes: [],
      taskCubesHidden: [],
      taskFilteredCubes: [],
      taskFilteredCubesHidden: [],
      taskDims: [],
      taskDimsHidden: [],
      dimSelection: {
        name: null,
        elements: [],
        aliases: [],
        selection: null,
        selectionType: null,
        selectionAlias: null,
      },
      dimSelectionIndex: null,
      dimSelectionType: null,
      taskCheckValid: false,
      taskViewControlObjects: true,
      taskSelectAll: false,
      taskSourceCubeList: [],
      mappingSourceDim: null,
      mappingTargetDim: null,
    };
  },
  methods: {
    handleError: Helper.handleError,
    trim: Helper.trim,
    /**
     * Deletes the task
     * @function
     */
    async deleteTask() {
      const loading = this.$loading({
        lock: true,
        //text: "Working",
        background: "rgba(0, 0, 0, 0.3)",
      });
      try {
        await TaskService.delete({
          taskId: this.taskId,
        });
        this.$store.dispatch("setCurrentTaskName", null);
        this.$store.dispatch("setTaskId", null);
        this.$message({
          type: "success",
          message: `"${this.taskName}" task deleted successfully`,
          duration: 5000,
          showClose: true,
        });
        this.$router.push({
          name: "tasks",
          params: { instanceId: this.instanceId },
        });
      } catch (err) {
        this.handleError(err, this, this.instanceId);
      }
      loading.close();
    },
    /**
     * Updates the interface after modifying the list of cubes
     * @function
     */
    cubeListUpdated(sourceCubeList) {
      this.taskSourceCubeList = sourceCubeList;
    },
    /**
     * Updates the interface after modifying the select all cubes option
     * @function
     */
    selectAllUpdated(selectAll) {
      this.taskSelectAll = selectAll;
    },
    /**
     * Shows or hides the control objects
     * @function
     */
    async viewControlObjectsUpdated() {
      const loading = this.$loading({
        lock: true,
        //text: "Working",
        background: "rgba(0, 0, 0, 0.3)",
      });
      try {
        [this.taskCubes, this.taskCubesHidden] = [
          this.taskCubesHidden,
          this.taskCubes,
        ];
        [this.taskFilteredCubes, this.taskFilteredCubesHidden] = [
          this.taskFilteredCubesHidden,
          this.taskFilteredCubes,
        ];
        [this.taskDims, this.taskDimsHidden] = [
          this.taskDimsHidden,
          this.taskDims,
        ];
        this.taskViewControlObjects = !this.taskViewControlObjects;

        const cubeResponse = await TaskService.filterCubes({
          instanceId: this.instanceId,
          dims: this.taskSourceDims
            .filter((dim) => dim.name)
            .map((dim) => dim.name),
          controlCubes: this.taskViewControlObjects,
        });
        this.taskFilteredCubes = cubeResponse.data.cubes;
      } catch (err) {
        this.handleError(err, this, this.instanceId);
      } finally {
        loading.close();
      }
    },
    async getDetails() {
      const loading = this.$loading({
        lock: true,
        //text: "Working",
        background: "rgba(0, 0, 0, 0.3)",
      });
      try {
        await this.getOptions();

        const response = await TaskService.getDetails({
          taskId: this.taskId,
        });
        const task = response.data.task;

        this.taskName = task.name;
        this.taskDescription = task.description;
        this.taskType = task.type;
        this.taskSourceType = task.source;
        this.taskTargetType = task.target;
        this.taskMethod = task.method;
        this.taskFactor = task.factor;
        this.taskSourceValue = task.value === null ? 0 : task.value;
        this.taskSelectAll = task.controlCubes;

        this.taskSourceCube =
          this.taskSourceType === "Single Cube"
            ? response.data.sourceTaskCubes[0].name
            : null;
        this.taskSourceCubeDims = [];
        await this.getCubeDims({
          card: "source",
          cube: this.taskSourceCube,
        });
        response.data.sourceCubeTaskDims.forEach((dim) => {
          for (let i = 0; i < this.taskSourceCubeDims.length; i++) {
            if (this.taskSourceCubeDims[i].name === dim.name) {
              this.$set(this.taskSourceCubeDims, i, {
                name: dim.name,
                display:
                  dim.selectionType === "Multi-Select"
                    ? dim.selection.map((el) => el.name).join(", ")
                    : dim.selection,
                elements: this.taskSourceCubeDims[i].elements,
                aliases: this.taskSourceCubeDims[i].aliases,
                hierarchy: dim.hierarchy,
                selection: dim.selection,
                selectionType: dim.selectionType,
                selectionAlias: dim.selectionAlias,
              });
            }
          }
        });

        this.taskTargetCube = response.data.targetTaskCube
          ? response.data.targetTaskCube.name
          : null;
        this.taskTargetCubeDims = [];
        await this.getCubeDims({
          card: "target",
          cube: this.taskTargetCube,
        });
        response.data.targetCubeTaskDims.forEach((dim) => {
          for (let i = 0; i < this.taskTargetCubeDims.length; i++) {
            if (this.taskTargetCubeDims[i].name === dim.name) {
              this.$set(this.taskTargetCubeDims, i, {
                name: dim.name,
                display:
                  dim.selectionType === "Multi-Select"
                    ? dim.selection.map((el) => el.name).join(", ")
                    : dim.selection,
                elements: this.taskTargetCubeDims[i].elements,
                aliases: this.taskTargetCubeDims[i].aliases,
                selection: dim.selection,
                hierarchy: dim.hierarchy,
                selectionType: dim.selectionType,
                selectionAlias: dim.selectionAlias,
              });
            }
          }
        });

        if (this.taskSourceType === "Multiple Cubes") {
          this.taskSourceCubeList = !task.selectAll
            ? response.data.sourceTaskCubes.map((cube) => cube.name)
            : task.controlCubes
            ? this.taskFilteredCubes
            : this.taskFilteredCubesHidden;
        }

        this.taskSourceDims = [];
        this.taskTargetDims = [];
        response.data.sourceTaskDims.forEach(async (sourceDim, index) => {
          this.taskSourceDims.push({
            name: sourceDim.name,
          });
          this.taskTargetDims.push({});
          await this.dimAdded(index);
          this.$set(this.taskSourceDims, index, {
            name: sourceDim.name,
            display:
              sourceDim.selectionType === "Multi-Select"
                ? sourceDim.selection.map((el) => el.name).join(", ")
                : sourceDim.selection,
            elements: this.taskSourceDims[index].elements,
            aliases: this.taskSourceDims[index].aliases,
            selection: sourceDim.selection,
            hierarchy: sourceDim.hierarchy,
            selectionType: sourceDim.selectionType,
            selectionAlias: sourceDim.selectionAlias,
          });
          response.data.targetTaskDims.forEach((targetDim) => {
            if (sourceDim.name === targetDim.name) {
              this.$set(this.taskTargetDims, index, {
                name: targetDim.name,
                display:
                  targetDim.selectionType === "Multi-Select"
                    ? targetDim.selection.map((el) => el.name).join(", ")
                    : targetDim.selection,
                elements: this.taskSourceDims[index].elements,
                aliases: this.taskSourceDims[index].aliases,
                selection: targetDim.selection,
                hierarchy: targetDim.hierarchy,
                selectionType: targetDim.selectionType,
                selectionAlias: targetDim.selectionAlias,
              });
            }
          });
        });
      } catch (err) {
        this.handleError(err, this, this.instanceId);
      } finally {
        loading.close();
      }
    },
    /**
     * Updates the interface after selecting a dimension
     * @function
     * @param {{dim: String, index: Number, type: String}} - Object containing dimension name, index, and type
     */
    dimSelected({ dim, index, type }) {
      this.dimSelection = dim;
      this.dimSelectionIndex = index;
      this.dimSelectionType = type;
      if (type.includes("target")) {
        this.placeholder =
          this.taskSourceType === "Multiple Cubes"
            ? this.taskSourceDims[index]
            : this.taskSourceCubeDims[index];
      } else {
        this.placeholder = null;
      }
      this.$bvModal.show("modal-selector");
    },
    selectionSubmitted({
      type,
      index,
      selection,
      hierarchy,
      selectionType,
      selectionAlias,
    }) {
      let updatedDim = {
        display:
          selectionType === "Multi-Select"
            ? (Array.isArray(selection) ? selection : JSON.parse(selection))
                .map((el) => el.name)
                .join(", ")
            : selection,
        selection,
        hierarchy,
        selectionType,
        selectionAlias,
      };
      if (type === "sourceCube") {
        updatedDim.name = this.taskSourceCubeDims[index].name;
        updatedDim.elements = this.taskSourceCubeDims[index].elements;
        updatedDim.aliases = this.taskSourceCubeDims[index].aliases;
        this.$set(this.taskSourceCubeDims, index, updatedDim);
      } else if (type === "source") {
        updatedDim.name = this.taskSourceDims[index].name;
        updatedDim.elements = this.taskSourceDims[index].elements;
        updatedDim.aliases = this.taskSourceDims[index].aliases;
        this.$set(this.taskSourceDims, index, updatedDim);
      } else if (type === "targetCube") {
        updatedDim.name = this.taskTargetCubeDims[index].name;
        updatedDim.elements = this.taskTargetCubeDims[index].elements;
        updatedDim.aliases = this.taskTargetCubeDims[index].aliases;
        this.$set(this.taskTargetCubeDims, index, updatedDim);
      } else if (type === "target") {
        updatedDim.name = this.taskTargetDims[index].name;
        updatedDim.elements = this.taskTargetDims[index].elements;
        updatedDim.aliases = this.taskTargetDims[index].aliases;
        this.$set(this.taskTargetDims, index, updatedDim);
      }
    },
    /**
     * Updates the interface after modifying the task sourceValue
     * @function
     * @param {String} sourceValue - Source value of the task
     */
    sourceValueUpdated(sourceValue) {
      this.taskSourceValue = sourceValue;
    },
    /**
     * Updates the interface after modifying the task name
     * @function
     * @param {String} name - Name of the task
     */
    nameUpdated(name) {
      this.taskName = name;
    },
    /**
     * Updates the interface after modifying the task description
     * @function
     * @param {String} description - Description of the task
     */
    descriptionUpdated(description) {
      this.taskDescription = description;
    },
    /**
     * Updates the interface after modifying the task factor
     * @function
     * @param {String} factor - Factor of the multiply task
     */
    factorUpdated(factor) {
      this.taskFactor = factor;
    },
    /**
     * Updates the interface after selecting a task type
     * @function
     * @param {String} type - Type of the task
     */
    typeSelected(type) {
      console.log("type updated");
      this.taskType = type;
      // this.taskSourceDims = [];
      // this.taskSourceCubeDims = [];
      // this.taskTargetDims = [];
      // this.taskTargetCubeDims = [];
      if (this.taskType === "Zero Out") {
        this.taskSourceType = "Value";
      } else if (
        ["Copy", "Move"].includes(this.taskType) &&
        this.taskSourceType === "Value"
      ) {
        this.taskSourceType = "Single Cube";
      }
      if (this.taskType === "Zero Out") {
        this.taskTargetType = "Cube";
      } else {
        if (this.taskSourceType === "Multiple Cubes") {
          this.taskTargetType = "Multiple Cubes";
        } else if (this.taskSourceType === "Value") {
          this.taskTargetType = "Cube";
        } else if (this.taskSourceType === "Single Cube") {
          if (this.taskTargetType === "Cube") {
            this.taskTargetType = "Same Cube";
          }
        }
      }
    },
    /**
     * Updates the interface after selecting a task source type
     * @function
     * @param {String} type - Type of the task source
     */
    sourceTypeSelected(sourceType) {
      this.taskSourceType = sourceType;
      if (this.taskSourceType === "Single Cube") {
        this.taskTargetType = "Same Cube";
      } else if (this.taskSourceType === "Value") {
        this.taskTargetType = "Cube";
      }
    },
    /**
     * Updates the interface after selecting a task target type
     * @function
     * @param {String} type - Type of the task target
     */
    targetTypeSelected(targetType) {
      this.taskTargetType = targetType;
    },
    /**
     * Updates the interface after selecting a task insertion method
     * @function
     * @param {String} method - Insertion method of the task target
     */
    methodSelected(method) {
      this.taskMethod = method;
    },

    showMappingPreview({ index, sourceDim, targetDim }) {
      if (!sourceDim) sourceDim = this.taskSourceCubeDims[index];
      if (!targetDim) targetDim = this.taskTargetCubeDims[index];
      this.mappingSourceDim = sourceDim;
      this.mappingTargetDim = targetDim;
      this.$bvModal.show("modal-mapping");
    },
    /**
     * Fetches the list of dimensions associated with a particular cube
     * @function
     * @param {{card: String, cube: String}} - Object containing card type and cube name
     */
    async getCubeDims({ card, cube }) {
      if (!cube) return;

      const loading = this.$loading({
        lock: true,
        //text: "Working",
        background: "rgba(0, 0, 0, 0.3)",
      });
      try {
        const dimResponse = await TaskService.getCubeDims({
          cubeName: cube,
          instanceId: this.instanceId,
        });
        let cubeDims = [];
        cubeDims = dimResponse.data.dims.value.map((dim) => {
          return {
            name: dim.Name,
            elements: [],
            aliases: [],
            hierarchy: dim.Name,
            selection: null,
            selectionType: null,
            selectionAlias: null,
          };
        });
        for (let i = 0; i < cubeDims.length; i++) {
          const aliasResponse = await TaskService.getDimAliases({
            dimName: cubeDims[i].name,
            instanceId: this.instanceId,
          });
          cubeDims[i].aliases = aliasResponse.data.aliases;
        }
        if (card === "source") {
          this.taskSourceCube = cube;
          this.taskSourceCubeDims = cubeDims;
        } else if (card === "target") {
          this.taskTargetCube = cube;
          this.taskTargetCubeDims = cubeDims;
        }
      } catch (err) {
        this.handleError(err, this, this.instanceId);
      } finally {
        loading.close();
      }
    },
    addDim() {
      if (
        this.taskSourceDims.length < 256 &&
        this.taskTargetDims.length < 256
      ) {
        this.taskSourceDims.push({
          name: null,
          elements: [],
          aliases: [],
          selection: null,
          hierarchy: null,
          selectionType: null,
          selectionAlias: null,
        });
        this.taskTargetDims.push({
          name: null,
          elements: [],
          aliases: [],
          selection: null,
          hierarchy: null,
          selectionType: null,
          selectionAlias: null,
        });
      }
    },
    async removeDim(index) {
      const loading = this.$loading({
        lock: true,
        //text: "Working",
        background: "rgba(0, 0, 0, 0.3)",
      });
      try {
        this.taskSourceDims.splice(index, 1);
        this.taskTargetDims.splice(index, 1);

        if (this.taskSourceDims.length === 0) {
          this.taskFilteredCubes = this.taskCubes;
          this.taskFilteredCubesHidden = this.taskCubesHidden;

          this.taskSourceCubeList = this.taskFilteredCubesHidden;
        } else {
          const cubeResponse = await TaskService.filterCubes({
            instanceId: this.instanceId,
            dims: this.taskSourceDims
              .filter((dim) => dim.name)
              .map((dim) => dim.name),
            controlCubes: this.taskViewControlObjects,
          });

          cubeResponse.data.cubes.forEach((cube) => {
            if (
              !this.taskFilteredCubes.includes(cube) &&
              cube.charAt(0) !== "}"
            )
              this.taskSourceCubeList.push(cube);
          });
          this.taskFilteredCubes = cubeResponse.data.cubes;
          if (this.taskSelectAll)
            this.taskSourceCubeList = this.taskFilteredCubes;
        }
      } catch (err) {
        this.handleError(err, this, this.instanceId);
      } finally {
        loading.close();
      }
    },
    async dimAdded(index) {
      const loading = this.$loading({
        lock: true,
        //text: "Working",
        background: "rgba(0, 0, 0, 0.3)",
      });
      try {
        const aliasResponse = await TaskService.getDimAliases({
          instanceId: this.instanceId,
          dimName: this.taskSourceDims[index].name,
        });
        this.$set(this.taskSourceDims, index, {
          name: this.taskSourceDims[index].name,
          elements: [],
          aliases: aliasResponse.data.aliases,
          selection: null,
          hierarchy: this.taskSourceDims[index].name,
          selectionType: null,
          selectionAlias: null,
        });
        this.$set(this.taskTargetDims, index, this.taskSourceDims[index]);

        const cubeResponse = await TaskService.filterCubes({
          instanceId: this.instanceId,
          dims: this.taskSourceDims
            .filter((dim) => dim.name)
            .map((dim) => dim.name),
          controlCubes: this.taskViewControlObjects,
        });
        this.taskFilteredCubes = cubeResponse.data.cubes;
      } catch (err) {
        this.handleError(err, this, this.instanceId);
      } finally {
        loading.close();
      }
    },
    /**
     * Runs the task
     * @function
     */
    async runTask() {
      const loading = this.$loading({
        lock: true,
        //text: "Working",
        background: "rgba(0, 0, 0, 0.3)",
      });
      try {
        if (this.envRights === "Run") {
          const message = `Are you sure you want to run the "${this.currentTaskName}" task?`;
          if (!confirm(message)) return;
        } else {
          const result = await this.saveTask({ runTask: true });
          if (!result) return;
        }
        if (!this.taskCheckValid) {
          let confirmed = true;
          const selected = this.taskTargetCubeDims.filter(
            (dim) => dim.selection
          );
          if (this.taskType === "Zero Out" && selected.length === 0) {
            confirmed = confirm(
              `This task will clear all data from the "${this.taskTargetCube}" cube. Click OK to continue.`
            );
          }
          if (confirmed) {
            await TaskService.run({
              taskId: this.taskId,
              userId: this.userId,
            });

            this.$message({
              type: "success",
              message: `The "${this.taskName}" task has been kicked off successfully. You'll receive an email when it finishes running. Check the task's history log for updates until then.`,
              duration: 5000,
              showClose: true,
            });
          }
        }
      } catch (err) {
        this.handleError(err, this, this.instanceId);
      } finally {
        loading.close();
      }
    },
    async runQuickTask() {
      this.taskCheckValid = true;
      const general = this.$refs.general.$v;
      const sourceCard = this.$refs.sourceCard.$v;
      const targetCard = this.$refs.targetCard.$v;

      if (
        !sourceCard.factor.$invalid &&
        !sourceCard.sourceCube.$invalid &&
        !sourceCard.sourceCubeDims.$invalid &&
        !sourceCard.sourceDims.$invalid &&
        !sourceCard.sourceValue.$invalid &&
        !targetCard.targetCube.$invalid &&
        !targetCard.targetCubeDims.$invalid &&
        !targetCard.targetDims.$invalid
      ) {
        const type = general.type.$model;
        const factor = sourceCard.factor.$model;
        const sourceType = sourceCard.sourceType.$model;
        const sourceCube = sourceCard.sourceCube.$model;
        const value = sourceCard.sourceValue.$model;
        const sourceCubeDims = sourceCard.sourceCubeDims.$model;
        const sourceDims = sourceCard.sourceDims.$model;
        const targetType = targetCard.targetType.$model;
        const targetCube = targetCard.targetCube.$model;
        const targetCubeDims = targetCard.targetCubeDims.$model;
        const targetDims = targetCard.targetDims.$model;

        let showError = false;
        if (type === "Move" && targetType !== "File") {
          if (
            (sourceType === "Single Cube" &&
              (targetCubeDims.findIndex((dim) => dim.selection) < 0 ||
                JSON.stringify(sourceCubeDims) ===
                  JSON.stringify(targetCubeDims))) ||
            (sourceType === "Multiple Cubes" &&
              (targetDims.findIndex((dim) => dim.selection) < 0 ||
                JSON.stringify(sourceDims) === JSON.stringify(targetDims)))
          ) {
            showError = true;
          }
        }

        if (showError) {
          this.$message({
            type: "warning",
            message:
              "Select at least one target dimension element that is different from the source selection.",
            duration: 7000,
            showClose: true,
          });
          return false;
        } else {
          const loading = this.$loading({
            lock: true,
            //text: "Working",
            background: "rgba(0, 0, 0, 0.3)",
          });
          try {
            let message = `Are you sure you want to run the quick task?`;
            const selected = this.taskTargetCubeDims.filter(
              (dim) => dim.selection
            );
            if (this.taskType === "Zero Out" && selected.length === 0) {
              message = `Are you sure you want to run the quick task? This will clear all data from the "${this.taskTargetCube}" cube.`;
            }
            if (confirm(message)) {
              await TaskService.runQuick({
                userId: this.userId,
                task: {
                  instanceId: this.instanceId,
                  type: this.taskType,
                  source: sourceType,
                  target: targetType,
                  factor: sourceType === "Value" ? null : factor,
                  value: sourceType === "Value" ? value : null,
                  method:
                    type === "Zero Out" || targetType === "File"
                      ? null
                      : this.taskMethod,
                  createdBy: this.userId,
                  updatedBy: this.userId,
                  selectAll:
                    sourceType === "Multiple Cubes" ? this.taskSelectAll : null,
                  controlCubes: this.taskSelectAll
                    ? this.taskViewControlObjects
                    : null,
                },
                sourceCubes: sourceType.includes("Cube")
                  ? this.mapTaskCubes(
                      sourceType === "Single Cube"
                        ? [sourceCube]
                        : this.taskSelectAll
                        ? []
                        : this.taskSourceCubeList
                    )
                  : [{ type: "source", name: null }],
                sourceViewDims:
                  sourceType === "Single Cube"
                    ? this.mapTaskDims(sourceCubeDims, "source")
                    : sourceType === "Multiple Cubes"
                    ? this.mapTaskDims(sourceDims, "source")
                    : [],
                targetCube: {
                  type: "target",
                  name: targetType.endsWith("Cube") ? targetCube : null,
                },
                targetViewDims: targetType.endsWith("Cube")
                  ? this.mapTaskDims(targetCubeDims, "target")
                  : targetType === "Multiple Cubes"
                  ? this.mapTaskDims(targetDims, "target")
                  : [],
              });
              this.$message({
                type: "success",
                // message: "Quick task completed successfully",
                message:
                  "The quick task has been kicked off successfully. You will receive an email when it finishes running.",
                duration: 5000,
                showClose: true,
              });
            }
          } catch (err) {
            this.handleError(err, this, this.instanceId);
          }
          loading.close();
        }
      } else {
        this.$message({
          type: "warning",
          message: "More information is required to run the task.",
          duration: 7000,
          showClose: true,
        });
      }
    },
    mapTaskDims(dims, dimType) {
      return dims.map((dim, index) => {
        return {
          name: dim.name,
          type: dimType,
          index,
          selection: dim.selection,
          hierarchy: dim.hierarchy,
          selectionType: dim.selectionType,
          selectionAlias: dim.selectionAlias,
        };
      });
    },
    mapTaskCubes(cubes) {
      return cubes.map((cube) => {
        return {
          type: "source",
          name: cube,
        };
      });
    },
    async saveTask({ runTask = false, name = null, description = null }) {
      let confirmed = true;
      if (!name) {
        let message = `Are you sure you want to save over the "${this.currentTaskName}" task?`;
        if (runTask) {
          message = `Are you sure you want to save over and then run the "${this.taskName}" task?`;
        }
        confirmed = confirm(message);
      }

      if (confirmed) {
        this.taskCheckValid = true;
        const general = this.$refs.general.$v;
        const sourceCard = this.$refs.sourceCard.$v;
        const targetCard = this.$refs.targetCard.$v;

        if (
          (name || !general.name.$invalid) &&
          !sourceCard.factor.$invalid &&
          !sourceCard.sourceCube.$invalid &&
          !sourceCard.sourceCubeDims.$invalid &&
          !sourceCard.sourceDims.$invalid &&
          !sourceCard.sourceValue.$invalid &&
          !targetCard.targetCube.$invalid &&
          !targetCard.targetCubeDims.$invalid &&
          !targetCard.targetDims.$invalid
        ) {
          let id = null;
          if (!name) {
            id = this.taskId;
            name = general.name.$model;
            description = this.taskDescription;
          }
          const type = general.type.$model;
          const factor = sourceCard.factor.$model;
          const sourceType = sourceCard.sourceType.$model;
          const sourceCube = sourceCard.sourceCube.$model;
          const value = sourceCard.sourceValue.$model;
          const sourceCubeDims = sourceCard.sourceCubeDims.$model;
          const sourceDims = sourceCard.sourceDims.$model;
          const targetType = targetCard.targetType.$model;
          const targetCube = targetCard.targetCube.$model;
          const targetCubeDims = targetCard.targetCubeDims.$model;
          const targetDims = targetCard.targetDims.$model;

          let showError = false;
          if (type === "Move" && targetType !== "File") {
            if (
              (sourceType === "Single Cube" &&
                (targetCubeDims.findIndex((dim) => dim.selection) < 0 ||
                  JSON.stringify(sourceCubeDims) ===
                    JSON.stringify(targetCubeDims))) ||
              (sourceType === "Multiple Cubes" &&
                (targetDims.findIndex((dim) => dim.selection) < 0 ||
                  JSON.stringify(sourceDims) === JSON.stringify(targetDims)))
            ) {
              showError = true;
            }
          }

          if (showError) {
            this.$message({
              type: "warning",
              message:
                "Select at least one target dimension element that is different from the source selection.",
              duration: 7000,
              showClose: true,
            });
            return false;
          } else {
            const loading = this.$loading({
              lock: true,
              //text: "Working",
              background: "rgba(0, 0, 0, 0.3)",
            });
            try {
              const response = await TaskService.save({
                userId: this.userId,
                reqTask: {
                  id,
                  instanceId: this.instanceId,
                  name: this.trim(name),
                  description: this.trim(description) || null,
                  type,
                  source: sourceType,
                  target: targetType,
                  factor: sourceType === "Value" ? null : factor,
                  value: sourceType === "Value" ? value : null,
                  method:
                    type === "Zero Out" || targetType === "File"
                      ? null
                      : this.taskMethod,
                  createdBy: this.userId,
                  updatedBy: this.userId,
                  selectAll:
                    sourceType === "Multiple Cubes" ? this.taskSelectAll : null,
                  controlCubes: this.taskSelectAll
                    ? this.taskViewControlObjects
                    : null,
                },
                sourceCubes: sourceType.includes("Cube")
                  ? this.mapTaskCubes(
                      sourceType === "Single Cube"
                        ? [sourceCube]
                        : this.taskSelectAll
                        ? []
                        : this.taskSourceCubeList
                    )
                  : [{ type: "source", name: null }],
                sourceCubeDims:
                  sourceType === "Single Cube"
                    ? this.mapTaskDims(sourceCubeDims, "source")
                    : [],
                sourceDims:
                  sourceType === "Multiple Cubes"
                    ? this.mapTaskDims(sourceDims, "source")
                    : [],
                targetCube: {
                  type: "target",
                  name: targetType.endsWith("Cube") ? targetCube : null,
                },
                targetCubeDims: targetType.endsWith("Cube")
                  ? this.mapTaskDims(targetCubeDims, "target")
                  : [],
                targetDims:
                  targetType === "Multiple Cubes"
                    ? this.mapTaskDims(targetDims, "target")
                    : [],
              });

              this.taskName = name;
              this.taskDescription = description;
              this.$store.dispatch("setTaskId", response.data.task.id);
              this.$store.dispatch("setCurrentTaskName", this.taskName);
              if (!runTask) {
                this.$message({
                  type: "success",
                  message: `"${this.taskName}" task saved successfully`,
                  duration: 5000,
                  showClose: true,
                });
              }
              this.taskCheckValid = false;
              return true;
            } catch (err) {
              this.handleError(err, this, this.instanceId);
            } finally {
              if (!runTask) loading.close();
            }
          }
        } else {
          this.$message({
            type: "warning",
            message: "More information is required to save the task.",
            duration: 7000,
            showClose: true,
          });
        }
      } else {
        return false;
      }
    },
    async getOptions() {
      try {
        const cubeResponse = await TaskService.getCubes({
          instanceId: this.instanceId,
        });
        this.taskCubes = cubeResponse.data.cubes.value.map((cube) => {
          return cube.Name;
        });
        this.taskCubesHidden = this.taskCubes.filter(
          (cube) => cube.charAt(0) !== "}"
        );
        this.taskFilteredCubes = this.taskCubes;
        this.taskFilteredCubesHidden = this.taskCubesHidden;

        const dimResponse = await TaskService.getDims({
          instanceId: this.instanceId,
        });
        this.taskDims = dimResponse.data.dims.value.map((dim) => {
          return dim.Name;
        });
        this.taskDimsHidden = this.taskDims.filter(
          (dim) => dim.charAt(0) !== "}"
        );
      } catch (err) {
        this.handleError(err, this, this.instanceId);
      }
    },
  },
  async mounted() {
    if (!this.isUserLoggedIn) {
      this.$router.push({ name: "login" });
    } else if (!this.instanceId) {
      this.$router.push({ name: "servers" });
    } else {
      this.getDetails();
    }
  },
  beforeDestroy() {
    if (this.taskId) this.$store.dispatch("setTaskId", null);
    if (this.currentTaskName) this.$store.dispatch("setCurrentTaskName", null);
  },
};
</script>

<style scoped></style>
